import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { fetchPostBySlug, stripHtml, formatDate } from '../../lib/wordpress';
import type { WPPost } from '../../lib/wordpress';
import CommonHeader from '../../components/feature/CommonHeader';
import CommonFooter from '../../components/feature/CommonFooter';

const BRAND = '#3E5F8F';
const SITE_NAME = 'さいわいデンタルクリニック moyuk SAPPORO';

function setMetaTag(selector: string, attr: string, value: string) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, attrName, attrVal] = selector.match(/\[(\w+(?::\w+)?)="([^"]+)"\]/) ?? [];
    if (attrName && attrVal) el.setAttribute(attrName, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/* 記事内の「この記事を書いた人」付近の画像を小さく丸くする */
function styleAuthorImages() {
  const content = document.querySelector('.column-content');
  if (!content) return;

  // テキストノードを走査して「この記事を書いた人」を含む要素を探す
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
  const targets: HTMLElement[] = [];
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    if (node.textContent?.includes('この記事を書いた人')) {
      const parent = node.parentElement;
      if (parent) targets.push(parent);
    }
  }

  targets.forEach((el) => {
    // 前の兄弟要素を遡って img を探す
    let sibling: Element | null = el.previousElementSibling;
    while (sibling) {
      const img =
        sibling.tagName === 'IMG'
          ? (sibling as HTMLImageElement)
          : sibling.querySelector('img');
      if (img) {
        img.style.width = '80px';
        img.style.height = '80px';
        img.style.borderRadius = '50%';
        img.style.objectFit = 'cover';
        img.style.margin = '0 auto';
        img.style.display = 'block';
        return;
      }
      sibling = sibling.previousElementSibling;
    }

    // 親要素の前の兄弟も探す
    let parent: Element | null = el.parentElement;
    while (parent && parent !== content) {
      let pSibling: Element | null = parent.previousElementSibling;
      while (pSibling) {
        const img =
          pSibling.tagName === 'IMG'
            ? (pSibling as HTMLImageElement)
            : pSibling.querySelector('img');
        if (img) {
          img.style.width = '80px';
          img.style.height = '80px';
          img.style.borderRadius = '50%';
          img.style.objectFit = 'cover';
          img.style.margin = '0 auto';
          img.style.display = 'block';
          return;
        }
        pSibling = pSibling.previousElementSibling;
      }
      parent = parent.parentElement;
    }
  });
}

export default function ColumnDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<WPPost | null | undefined>(undefined);

  useEffect(() => {
    if (!slug) return;
    setPost(undefined);
    fetchPostBySlug(slug).then(setPost);
  }, [slug]);

  useEffect(() => {
    if (!post) return;

    const title = `${stripHtml(post.title.rendered)} | ${SITE_NAME}`;
    const description = stripHtml(post.excerpt.rendered).slice(0, 120);
    const canonical = `https://saiwaidental.jp/moyuk/column/${post.slug}/`;
    const thumbnail = post._embedded?.['wp:featuredmedia']?.[0]?.source_url ?? 'https://saiwaidental.jp/moyuk/images/ogp.jpg';

    document.title = title;
    setCanonical(canonical);
    setMetaTag('meta[name="description"]', 'content', description);
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', canonical);
    setMetaTag('meta[property="og:type"]', 'content', 'article');
    setMetaTag('meta[property="og:image"]', 'content', thumbnail);
    setMetaTag('meta[name="twitter:title"]', 'content', title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);
    setMetaTag('meta[name="twitter:image"]', 'content', thumbnail);

    // レンダリング完了後に著者画像を整形（2回試行）
    requestAnimationFrame(() => styleAuthorImages());
    setTimeout(() => styleAuthorImages(), 300);
  }, [post]);

  if (post === undefined) {
    return (
      <div className="min-h-screen bg-white">
        <CommonHeader />
        <div className="pt-40 flex justify-center">
          <div className="space-y-4 w-full max-w-3xl px-6">
            <div className="bg-gray-100 h-8 w-2/3 rounded animate-pulse" />
            <div className="bg-gray-100 h-4 w-full rounded animate-pulse" />
            <div className="bg-gray-100 h-4 w-5/6 rounded animate-pulse" />
            <div className="bg-gray-100 h-64 rounded animate-pulse mt-6" />
          </div>
        </div>
      </div>
    );
  }

  if (post === null) {
    return <Navigate to="/column/" replace />;
  }

  const category = post._embedded?.['wp:term']?.[0]?.[0];

  return (
    <div className="min-h-screen bg-white">
      <CommonHeader />

      <main className="pt-28 pb-24 px-6 max-w-3xl mx-auto">
        <nav className="text-xs text-gray-400 mb-6 flex flex-wrap items-center gap-1">
          <Link to="/" className="hover:text-gray-600 transition-colors">TOP</Link>
          <span>/</span>
          <Link to="/column/" className="hover:text-gray-600 transition-colors">コラム</Link>
          <span>/</span>
          <span className="text-gray-500 line-clamp-1" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
        </nav>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          {category && (
            <span className="text-xs px-3 py-1 rounded-full text-white" style={{ backgroundColor: BRAND }}>
              {category.name}
            </span>
          )}
          <time className="text-xs text-gray-400">{formatDate(post.date)}</time>
        </div>

        <h1
          className="text-2xl md:text-3xl font-bold text-gray-900 leading-snug mb-8"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
        />

        <div className="column-content" dangerouslySetInnerHTML={{ __html: post.content.rendered }} />

        <div className="mt-14 p-6 rounded-xl text-white text-center" style={{ backgroundColor: BRAND }}>
          <p className="font-bold text-lg mb-1">まずは無料診断から</p>
          <p className="text-sm opacity-90 mb-4">治療を急かさない。あなたのペースで判断できる環境を整えています。</p>
          <a
            href="https://reservation.stransa.co.jp/8878b04a60c23f1ce5b3268e265263cb"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-sm font-bold px-8 py-3 rounded-full hover:opacity-90 transition-opacity"
            style={{ color: BRAND }}
          >
            ご相談予約はこちら
          </a>
        </div>

        <div className="mt-10 pt-8 border-t border-gray-100">
          <Link to="/column/" className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: BRAND }}>
            ← コラム一覧に戻る
          </Link>
        </div>
      </main>

      <CommonFooter />
    </div>
  );
}
